<template>
  <v-card>
    <v-card-subtitle v-if="title">{{ title }}</v-card-subtitle>
    <v-card-text>
      <v-row>
        <v-col cols="12" :sm="showPreview ? 6 : 12" :md="showPreview ? 6 : 12">
          <v-file-input
            v-model="selectedFile"
            :rules="validationRules"
            outlined
            hide-details="auto"
            chips
            show-size
            clearable
            :accept="acceptedFileTypesString"
            :label="label"
            :prepend-inner-icon="prependInnerIcon"
            prepend-icon=""
            type="file"
            :disabled="loading"
            @click:clear="onFileClear"
            @change="onFileChange"
          />
          <div class="mt-4">
            <v-btn
              :disabled="!selectedFile || !isFileValid || loading"
              :loading="loading"
              color="primary"
              depressed
              @click="uploadFile"
            >
              <v-icon left>mdi-upload</v-icon>
              {{ uploadButtonText }}
            </v-btn>
          </div>
          <div v-if="hint" class="mt-2 text-caption text--secondary">
            {{ hint }}
          </div>
        </v-col>
        <v-col v-if="showPreview && filePreviewUrl" cols="12" sm="6" md="6">
          <v-card outlined>
            <v-card-subtitle>Vorschau</v-card-subtitle>
            <v-card-text>
              <v-img
                :src="filePreviewUrl"
                contain
                :height="previewHeight"
                class="mb-2"
              />
            </v-card-text>
          </v-card>
        </v-col>
        <v-col
          v-if="showPreview && currentImageUrl && !filePreviewUrl"
          cols="12"
          sm="6"
          md="6"
        >
          <v-card outlined>
            <v-card-subtitle>Aktuelles Bild</v-card-subtitle>
            <v-card-text>
              <v-img
                :src="currentImageUrl"
                contain
                :height="previewHeight"
                class="mb-2"
              />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-card-text>
    <v-snackbar v-model="snackbar.show" :timeout="5000" :color="snackbar.color">
      {{ snackbar.text }}
    </v-snackbar>
  </v-card>
</template>

<script>
import { storage, TaskEvent } from "@/firebase";

export default {
  name: "image-upload",
  props: {
    /**
     * The storage path where the file will be uploaded.
     * Example: "organizations/{organizationId}/dutyPositions/{itemId}"
     */
    storagePath: {
      type: String,
      required: true,
    },
    /**
     * The file name to use when storing the file (without extension).
     * Example: "dutyPosition" will become "dutyPosition.png"
     */
    fileName: {
      type: String,
      default: "image",
    },
    /**
     * Accepted file types as an array of MIME types.
     * Example: ["image/png", "image/jpeg"]
     */
    acceptedFileTypes: {
      type: Array,
      default: () => ["image/png", "image/jpeg"],
    },
    /**
     * Maximum file size in bytes.
     * Default: 2MB (2000000 bytes)
     */
    maxFileSize: {
      type: Number,
      default: 2000000,
    },
    /**
     * Label for the file input field.
     */
    label: {
      type: String,
      default: "Datei auswählen",
    },
    /**
     * Title displayed above the upload section.
     */
    title: {
      type: String,
      default: "",
    },
    /**
     * Hint text displayed below the file input.
     */
    hint: {
      type: String,
      default: "",
    },
    /**
     * Text for the upload button.
     */
    uploadButtonText: {
      type: String,
      default: "Hochladen",
    },
    /**
     * Icon for the file input field.
     */
    prependInnerIcon: {
      type: String,
      default: "mdi-image",
    },
    /**
     * Whether to show image preview.
     */
    showPreview: {
      type: Boolean,
      default: true,
    },
    /**
     * Height of the preview image.
     */
    previewHeight: {
      type: [String, Number],
      default: "180px",
    },
    /**
     * Current image URL to display when no new file is selected.
     */
    currentImageUrl: {
      type: String,
      default: "",
    },
    /**
     * Success message shown in snackbar after successful upload.
     */
    successMessage: {
      type: String,
      default:
        "Bild erfolgreich hochgeladen. Es kann einige Minuten dauern, bis die Änderung aktiv wird.",
    },
    /**
     * Error message prefix shown in snackbar on upload error.
     */
    errorMessagePrefix: {
      type: String,
      default: "Fehler beim Hochladen:",
    },
    /**
     * Whether the field is required.
     */
    required: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedFile: null,
      filePreviewUrl: "",
      loading: false,
      isFileValid: false,
      snackbar: {
        show: false,
        text: "",
        color: "",
      },
    };
  },
  computed: {
    acceptedFileTypesString() {
      return this.acceptedFileTypes.join(", ");
    },
    formattedMaxFileSize() {
      if (this.maxFileSize >= 1000000) {
        return `${(this.maxFileSize / 1000000).toFixed(0)} MB`;
      }
      return `${(this.maxFileSize / 1000).toFixed(0)} KB`;
    },
    fileExtension() {
      if (!this.selectedFile) return "png";
      const type = this.selectedFile.type;
      if (type === "image/jpeg") return "jpg";
      if (type === "image/png") return "png";
      if (type === "image/gif") return "gif";
      if (type === "image/webp") return "webp";
      if (type === "image/svg+xml") return "svg";
      // Default to extension from filename
      const parts = this.selectedFile.name.split(".");
      return parts.length > 1 ? parts.pop() : "png";
    },
    validationRules() {
      const rules = [];

      if (this.required) {
        rules.push((value) => !!value || "Datei ist erforderlich.");
      }

      rules.push(
        (value) =>
          !value ||
          value.size <= this.maxFileSize ||
          `Dateigröße muss kleiner als ${this.formattedMaxFileSize} sein.`
      );

      rules.push((value) => {
        if (!value) return true;
        return (
          this.acceptedFileTypes.includes(value.type) ||
          `Datei muss vom Typ ${this.acceptedFileTypesLabels} sein.`
        );
      });

      return rules;
    },
    acceptedFileTypesLabels() {
      return this.acceptedFileTypes
        .map((type) => {
          const ext = type.split("/")[1].toUpperCase();
          return ext;
        })
        .join(" oder ");
    },
  },
  methods: {
    onFileChange() {
      if (this.selectedFile) {
        // Validate file
        this.isFileValid = this.validateFile(this.selectedFile);

        if (this.isFileValid) {
          // Generate preview URL
          this.filePreviewUrl = URL.createObjectURL(this.selectedFile);
          this.$emit("file-selected", this.selectedFile);
        } else {
          this.filePreviewUrl = "";
        }
      } else {
        this.filePreviewUrl = "";
        this.isFileValid = false;
      }
    },
    onFileClear() {
      this.filePreviewUrl = "";
      this.selectedFile = null;
      this.isFileValid = false;
      this.$emit("file-cleared");
    },
    validateFile(file) {
      if (!file) return false;

      // Check file size
      if (file.size > this.maxFileSize) {
        return false;
      }

      // Check file type
      if (!this.acceptedFileTypes.includes(file.type)) {
        return false;
      }

      return true;
    },
    uploadFile() {
      if (!this.selectedFile || !this.isFileValid) return;

      this.loading = true;
      this.$emit("upload-started");

      const file = this.selectedFile;
      const metadata = {
        contentType: file.type,
      };

      const storageRef = storage.ref();
      const fullPath = `${this.storagePath}/${this.fileName}.${this.fileExtension}`;

      const uploadTask = storageRef.child(fullPath).put(file, metadata);

      uploadTask.on(
        TaskEvent.STATE_CHANGED,
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          this.$emit("upload-progress", progress);

          switch (snapshot.state) {
            case TaskEvent.PAUSED:
              this.$emit("upload-paused");
              break;
            case TaskEvent.RUNNING:
              this.$emit("upload-running");
              break;
          }
        },
        (error) => {
          this.loading = false;
          this.showSnackbar(
            `${this.errorMessagePrefix} ${error.message}`,
            "error"
          );
          this.$emit("upload-error", error);
        },
        () => {
          // Upload completed successfully
          this.loading = false;

          uploadTask.snapshot.ref.getDownloadURL().then((downloadURL) => {
            this.showSnackbar(this.successMessage, "success");
            this.$emit("upload-complete", {
              downloadURL,
              path: fullPath,
              fileName: `${this.fileName}.${this.fileExtension}`,
            });

            // Clear the file after successful upload
            this.selectedFile = null;
            this.filePreviewUrl = "";
            this.isFileValid = false;
          });
        }
      );
    },
    showSnackbar(text, color = "") {
      this.snackbar.text = text;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },
    // Public method to reset the component
    reset() {
      this.selectedFile = null;
      this.filePreviewUrl = "";
      this.loading = false;
      this.isFileValid = false;
    },
  },
  beforeDestroy() {
    // Clean up the object URL to prevent memory leaks
    if (this.filePreviewUrl) {
      URL.revokeObjectURL(this.filePreviewUrl);
    }
  },
};
</script>
